import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useListingSearchStore } from '@/store';
import { BigSearchIcon } from '@/component/Icons/BigSearchIcon';
import { ButtonsBar, Modal } from '../../components/Modal';
import {
  Counter,
  GuestsPicker,
  LabeledCheckbox,
} from '../../components/GuestsPicker';
import { ButtonOutline, ButtonPrimary } from '../../components/Button';

export function GuestsModal({
  onSearch,
  onClearAll,
  loading,
  searchIcon = <BigSearchIcon size="26" color="white" />,
  buttonClearText,
  buttonSearchText,
}) {
  const t = useTranslations();
  const { items, values, disabledByType, onTogglePets, pets } = useGuests();
  return (
    <Modal>
      <h3 className="text-slate-800 text-2xl font-bold font-['Raleway'] leading-[30px] tracking-tight !mx-6 !mt-6 sm:max-md:!mx-10 max-md:font-semibold">
        {t('modal-mobile-guests-title')}
      </h3>
      <GuestsPicker
        onFooterClick={onTogglePets}
        footer={
          <LabeledCheckbox
            name="toggle-pets"
            title={t('pets-title')}
            subtext={t('pets-desc')}
            value={pets}
            onChange={onTogglePets}
          />
        }
      >
        {items.map((item) => (
          <Counter
            key={item.type}
            title={t(item.name)}
            subtext={t(item.constraints)}
            value={values[item.type]}
            onIncrement={item.onIncrement}
            onDecrement={item.onDecrement}
            decrementDisabled={disabledByType[item.type]}
          />
        ))}
      </GuestsPicker>
      <ButtonsBar className="!mt-auto">
        <ButtonOutline onClick={onClearAll} disabled={loading}>
          {buttonClearText || t('action-clear-all')}
        </ButtonOutline>
        <ButtonPrimary icon={searchIcon} onClick={onSearch} disabled={loading}>
          {buttonSearchText || t('search')}
        </ButtonPrimary>
      </ButtonsBar>
    </Modal>
  );
}

function useGuests() {
  const {
    incrementGuest,
    decrementGuest,
    guests,
    togglePets,
    pets,
    // TODO: remove this leaked abstraction
  } = useListingSearchStore();
  const [guestsItems] = useState(() => [
    {
      name: 'adults',
      constraints: 'age-adult',
      type: 'adults',
      onIncrement: () => incrementGuest('adults'),
      onDecrement: () => decrementGuest('adults'),
    },
    {
      name: 'children',
      constraints: 'age-children',
      type: 'children',
      onIncrement: () => incrementGuest('children'),
      onDecrement: () => decrementGuest('children'),
    },
    {
      name: 'infants',
      constraints: 'age-infant',
      type: 'infants',
      onIncrement: () => incrementGuest('infants'),
      onDecrement: () => decrementGuest('infants'),
    },
  ]);
  // TODO: move this business knowledge to the model
  const needAccompanying = guests.infants > 0 || guests.children > 0 || pets;
  const disabledByType = {
    adults: guests.adults <= 0 || (needAccompanying && guests.adults === 1),
    children: guests.children <= 0,
    infants: guests.infants <= 0,
  };
  const onTogglePets = useCallback(
    (e) => {
      e.preventDefault();
      togglePets(e.target.checked);
    },
    [togglePets]
  );
  return {
    items: guestsItems,
    values: guests,
    disabledByType,
    pets,
    onTogglePets,
  };
}
